<template>
  <ElFormItem prop="storm_frequencies" class="full-width storm-frequencies-container">
    <div class="storm-header d--f mb-15px">
      <label class="fw-500 font-18px">Design storm frequencies <span class="text-red">*</span></label>
      <div class="flex-spacer"></div>
      <AppButton
        v-if="store.formData.storm_frequencies.length < 3"
        variant="primary"
        size="small"
        @click="store.addStormFrequency"
        class="add-storm-btn"
      >
        <AppIcon color="white" type="plus" size="14" class="mr-5px" /> Add storm frequency
      </AppButton>
    </div>

    <p class="text-muted font-14px">
      Add up to 3 design storm frequencies. Each will have its own set of coefficients.
    </p>

    <div class="storm-container">
      <div v-for="(storm, index) in sortedStormFrequencies" :key="index" class="storm-card">
        <div class="storm-card-header d--f jc--sb ai--c">
          <span class="storm-number">Storm {{ index + 1 }}</span>
          <AppButton
            v-if="store.formData.storm_frequencies.length > 1"
            variant="outlined"
            @click="() => store.removeStormFrequency(index)"
            class="remove-btn"
            size="small"
          >
            <AppIcon color="primary" type="trash" size="16" />
          </AppButton>
        </div>

        <div class="storm-card-body">
          <div class="mb-15px">
            <label class="d--b mb-5px font-14px">Storm frequency</label>
            <AppSelect
              v-model="storm.frequency"
              :options="availableStormFrequencies(index)"
              placeholder="Select storm frequency"
              size="large"
              @change="() => handleStormFrequencyChange(index)"
              required
            />
          </div>

          <div class="mb-15px">
            <label class="d--b mb-5px font-14px">Storm duration</label>
            <AppSelect
              v-model="storm.duration"
              :options="durationOptions"
              placeholder="Select storm duration"
              size="large"
              @change="() => handleStormFrequencyChange(index)"
              required
            />
          </div>

          <div>
            <div class="mb-10px">
              <label class="d--b mb-5px font-14px">Rainfall intensity</label>
              <div class="d--f ai--c mt-8px">
                <AppInput
                  v-model="storm.rainfall_intensity"
                  type="number"
                  placeholder="Enter rainfall intensity (mm/hr)"
                  min="0"
                  step="0.1"
                  required
                  :loading="storm.loading"
                  :disabled="storm.loading"
                />
                <span class="text-muted ml-8px font-14px">mm/hr</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ElFormItem>
</template>

<script setup>
import { ElFormItem } from "element-plus";
import { AppButton, AppIcon, AppInput, AppSelect } from "@/components/ui";
import { useReportFormStore } from "@/stores/reportFormStore";
import { stormFrequencyOptions, durationOptions } from "@/utilities/collections/newReportOptions";
import IDFApi from "@/api/idf";
import { showToast } from "@/utilities/toast";
import { computed } from "vue";

const store = useReportFormStore();

// Sort storm frequencies by value
const sortedStormFrequencies = computed(() => {
  return [...store.formData.storm_frequencies].sort((a, b) => {
    if (!a.frequency || !b.frequency) return 0;
    const aValue = parseInt(a.frequency);
    const bValue = parseInt(b.frequency);
    return aValue - bValue;
  });
});

// Get available storm frequencies for a specific index
const availableStormFrequencies = (currentIndex) => {
  const currentStorm = store.formData.storm_frequencies[currentIndex];
  const usedFrequencyDurationPairs = store.formData.storm_frequencies
    .map((storm, index) => (index !== currentIndex ? `${storm.frequency}-${storm.duration}` : null))
    .filter(Boolean);

  // If current storm has no duration selected, show all frequencies
  if (!currentStorm.duration) {
    return stormFrequencyOptions;
  }

  // Filter out frequencies that are already used with the same duration
  return stormFrequencyOptions.filter(
    (option) => !usedFrequencyDurationPairs.includes(`${option.value}-${currentStorm.duration}`)
  );
};

const handleStormFrequencyChange = async (index) => {
  const storm = store.formData.storm_frequencies[index];

  if (!store.formData.latitude || !store.formData.longitude || !storm.frequency || !storm.duration) {
    return;
  }

  // Set loading state
  storm.loading = true;

  try {
    const response = await IDFApi.getInterpolatedData(store.formData.latitude, store.formData.longitude);

    // Extract rainfall intensity for the specific storm frequency and duration
    const returnPeriod = parseInt(storm.frequency);
    const durationMinutes = parseInt(storm.duration);

    // Find the matching data point for selected duration
    const matchingDataPoint = response.data_points.find(
      (point) => point.return_period_years === returnPeriod && point.duration_minutes === durationMinutes
    );

    if (matchingDataPoint) {
      storm.rainfall_intensity = matchingDataPoint.rainfall_rate_mm_hr;
      storm.rainfall_depth = matchingDataPoint.rainfall_mm;
    } else {
      // If exact match not found, try interpolation using the IDF curve formula
      if (response.interpolation && response.interpolation[returnPeriod]) {
        const params = response.interpolation[returnPeriod];
        const durationHours = durationMinutes / 60.0;
        const interpolatedIntensity = params.a * Math.pow(durationHours, params.b);
        storm.rainfall_intensity = parseFloat(interpolatedIntensity.toFixed(1));
        storm.rainfall_depth = parseFloat((storm.rainfall_intensity * durationHours).toFixed(2));
      } else {
        // If interpolation params not available, show error
        showToast(
          `No IDF data found for ${returnPeriod}-year, ${durationMinutes}-minute storm. Please enter rainfall intensity manually.`,
          { type: "warning" }
        );
      }
    }

    // Always fetch 15-minute intensity for peak flow calculation
    const fifteenMinDataPoint = response.data_points.find(
      (point) => point.return_period_years === returnPeriod && point.duration_minutes === 15
    );

    if (fifteenMinDataPoint) {
      storm.rainfall_intensity_15min = fifteenMinDataPoint.rainfall_rate_mm_hr;
    } else {
      // If 15-min data not found, try interpolation
      if (response.interpolation && response.interpolation[returnPeriod]) {
        const params = response.interpolation[returnPeriod];
        const durationHours = 15 / 60.0;
        const interpolatedIntensity = params.a * Math.pow(durationHours, params.b);
        storm.rainfall_intensity_15min = parseFloat(interpolatedIntensity.toFixed(1));
      } else {
        // Fallback: use the selected duration intensity if 15-min not available
        storm.rainfall_intensity_15min = storm.rainfall_intensity;
      }
    }
  } catch (error) {
    showToast("Failed to fetch rainfall intensity from IDF data. Please enter it manually.", { type: "error" });
  } finally {
    // Clear loading state
    storm.loading = false;
  }
};
</script>

<style lang="scss" scoped>
.text-red {
  color: $color-red;
}

.storm-container {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px 0px -10px;
  width: calc(100% + 20px);
}

.storm-card {
  flex: 0 0 calc(50% - 20px);
  margin: 20px 10px 0px 10px;
  background-color: white;
  border: 1px solid $color-grey-10;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.storm-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.storm-card-header {
  background-color: $color-grey-12;
  padding: 10px 15px;
  border-bottom: 1px solid $color-grey-10;
}

.storm-number {
  font-weight: 500;
  color: $color-grey-4;
}

.storm-card-body {
  padding: 15px;
}

.rainfall-intensity-display {
  background-color: $color-grey-12;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 10px;
}

.intensity-value {
  font-weight: 500;
  color: $color-primary;
}

.add-storm-btn {
  padding: 6px 12px;
  font-size: 14px;
}

.font-14px {
  font-size: 14px;
}

.font-18px {
  font-size: 18px;
}

.storm-header {
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.flex-spacer {
  flex-grow: 1;
}

.coefficient-label {
  width: 40px;
}

.storm-frequencies-container {
  max-width: 100%;
  overflow: hidden;
}

@media (max-width: 767px) {
  .storm-card {
    flex: 0 0 calc(100% - 20px);
  }
}
</style>
